import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled, { useTheme } from 'styled-components';
import type { RemoteBoundaryComponent } from '@dutchiesdk/ecommerce-extensions-sdk';

import { remoteComponentIsValid } from 'src/theme/remote-component-is-valid';
import type { CheckoutChromeDataInterface, CheckoutHeaderProps } from './checkout-theme-contract';
import { useCheckoutBridgeDataInterface, useCheckoutHeaderProps } from './checkout-bridge-data-interface';
import { useRemoteCheckoutTheme } from './use-remote-checkout-theme';

type RemoteCheckoutComponentProps = {
  component: RemoteBoundaryComponent;
  data: CheckoutChromeDataInterface;
  directProps?: CheckoutHeaderProps;
  theme: ReturnType<typeof useTheme>;
};

const RemoteCheckoutComponent: React.FC<RemoteCheckoutComponentProps> = ({ component, data, directProps, theme }) => {
  const RemoteComponent = component as React.FC<any>;
  return <RemoteComponent theme={theme} __dsdk__checkoutDataBridgeData={data} {...(directProps ?? {})} />;
};

type CheckoutHeaderFallbackProps = CheckoutHeaderProps & {
  location: CheckoutChromeDataInterface['location'];
};

const CheckoutHeaderFallback: React.FC<CheckoutHeaderFallbackProps> = ({ backLabel, location, onBack }) => (
  <FallbackHeaderContent>
    <FallbackBackButton type='button' aria-label={backLabel} onClick={onBack}>
      <span aria-hidden='true'>←</span>
      Back
    </FallbackBackButton>
    {location?.name && <FallbackStoreName>{location.name}</FallbackStoreName>}
  </FallbackHeaderContent>
);

const remoteErrorFallback = (componentName: string) => ({ error }: { error: Error }): null => {
  console.error(`Remote Component error (${componentName}):`, error);
  return null;
};

const remoteHeaderErrorFallback = (componentName: string, fallbackProps: CheckoutHeaderFallbackProps) => ({
  error,
}: {
  error: Error;
}): JSX.Element => {
  console.error(`Remote Component error (${componentName}):`, error);
  return <CheckoutHeaderFallback {...fallbackProps} />;
};

/**
 * Branding-only chrome around Dutchie-hosted checkout. Remote components are
 * siblings of the host-owned main region and can never wrap transaction UI.
 */
export function CheckoutThemeChrome({ children }: { children: React.ReactNode }): JSX.Element {
  const { CheckoutHeader, CheckoutFooter, decision, isLoading } = useRemoteCheckoutTheme();
  const checkoutBridgeData = useCheckoutBridgeDataInterface();
  const checkoutHeaderProps = useCheckoutHeaderProps();
  const styledTheme = useTheme();
  const ValidCheckoutHeader = remoteComponentIsValid(CheckoutHeader) ? CheckoutHeader : undefined;
  const ValidCheckoutFooter = remoteComponentIsValid(CheckoutFooter) ? CheckoutFooter : undefined;
  const discoveryPending = decision === 'pending';
  const checkoutThemeExpected = decision === 'remote';

  return (
    <>
      {discoveryPending && (
        <CheckoutDiscoveryPlaceholder role='status' aria-live='polite' aria-busy='true'>
          <VisuallyHidden>Loading checkout</VisuallyHidden>
        </CheckoutDiscoveryPlaceholder>
      )}
      <CheckoutExperience hidden={discoveryPending} aria-hidden={discoveryPending || undefined}>
        {checkoutThemeExpected && (
          <CheckoutHeaderSlot data-testid='checkout-theme-header-slot'>
            {!isLoading &&
              (ValidCheckoutHeader ? (
                <ErrorBoundary
                  fallbackRender={remoteHeaderErrorFallback(ValidCheckoutHeader.name, {
                    ...checkoutHeaderProps,
                    location: checkoutBridgeData.location,
                  })}
                  resetKeys={[ValidCheckoutHeader, checkoutHeaderProps.backLabel]}
                >
                  <RemoteCheckoutComponent
                    component={ValidCheckoutHeader}
                    data={checkoutBridgeData}
                    directProps={checkoutHeaderProps}
                    theme={styledTheme}
                  />
                </ErrorBoundary>
              ) : (
                <CheckoutHeaderFallback {...checkoutHeaderProps} location={checkoutBridgeData.location} />
              ))}
          </CheckoutHeaderSlot>
        )}
        <CheckoutMain key='main' id='checkout-main-content' tabIndex={-1}>
          {children}
        </CheckoutMain>
        {checkoutThemeExpected && !isLoading && ValidCheckoutFooter && (
          <ErrorBoundary fallbackRender={remoteErrorFallback(ValidCheckoutFooter.name)}>
            <footer>
              <RemoteCheckoutComponent component={ValidCheckoutFooter} data={checkoutBridgeData} theme={styledTheme} />
            </footer>
          </ErrorBoundary>
        )}
      </CheckoutExperience>
    </>
  );
}

const CheckoutDiscoveryPlaceholder = styled.div`
  flex: 1 0 auto;
  min-height: 100vh;
  width: 100%;
`;

const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const CheckoutExperience = styled.div`
  display: contents;

  &[hidden] {
    display: none;
  }
`;

const CheckoutHeaderSlot = styled.header`
  box-sizing: border-box;
  height: var(--dcm-checkout-theme-header-height, 76px);
  overflow: hidden;
  width: 100%;

  > * {
    height: 100%;
  }
`;

const FallbackHeaderContent = styled.div`
  align-items: center;
  background: var(--dcm-checkout-header-bg, #ffffff);
  border-bottom: 1px solid var(--dcm-checkout-header-border-color, #e5e7eb);
  box-sizing: border-box;
  display: flex;
  gap: 20px;
  padding: 12px 20px;

  @media (min-width: 768px) {
    gap: 32px;
    padding: 12px 40px;
  }
`;

const FallbackBackButton = styled.button`
  align-items: center;
  background: var(--dcm-checkout-back-button-bg, #111827);
  border: 2px solid var(--dcm-checkout-back-button-border-color, #111827);
  border-radius: var(--dcm-checkout-back-button-radius, 8px);
  color: var(--dcm-checkout-back-button-color, #ffffff);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  gap: 10px;
  min-height: 48px;
  padding: 10px 18px;

  &:focus-visible {
    outline: 3px solid var(--dcm-checkout-focus-color, #2563eb);
    outline-offset: 3px;
  }
`;

const FallbackStoreName = styled.span`
  color: var(--dcm-checkout-header-text-color, #111827);
  font-size: 18px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CheckoutMain = styled.main`
  flex: 1 0 auto;
  min-width: 0;
  width: 100%;
`;
